import type { ReactNode } from 'react';
import { Icon } from '../icons';
import type { StyleId } from '../../lib/types';

interface Props {
  value: StyleId | null;
  onChange: (s: StyleId) => void;
  onNext: () => void;
  onBack: () => void;
}

interface StyleOption {
  id: StyleId;
  name: string;
  desc: string;
  preview: ReactNode;
}

export function StyleScreen({ value, onChange, onNext, onBack }: Props) {
  const styles: StyleOption[] = [
    {
      id: 'classic',
      name: 'קלאסי חם',
      desc: 'מעברים רכים, מסגרות עדינות',
      preview: (
        <div
          style={{
            height: '100%',
            background: 'linear-gradient(135deg, oklch(0.85 0.06 60) 0%, oklch(0.92 0.04 50) 100%)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 14,
              background: 'white',
              borderRadius: 6,
              boxShadow: '0 4px 12px oklch(0 0 0 / 0.12)',
            }}
          />
        </div>
      ),
    },
    {
      id: 'cinematic',
      name: 'קולנועי',
      desc: 'תמונות מלאות, דרמטי',
      preview: (
        <div style={{ height: '100%', position: 'relative', overflow: 'hidden', background: '#1a1310' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 14, background: '#000' }} />
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 14, background: '#000' }} />
        </div>
      ),
    },
    {
      id: 'vintage',
      name: 'וינטג׳',
      desc: 'גוונים חמים, אורות זהב',
      preview: (
        <div
          style={{
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, oklch(0.78 0.08 60), oklch(0.6 0.1 40))',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(ellipse at center, transparent 40%, oklch(0.4 0.08 50 / 0.4) 100%)',
            }}
          />
        </div>
      ),
    },
    {
      id: 'modern',
      name: 'מודרני',
      desc: 'קצבי, גריד דינמי',
      preview: (
        <div
          style={{
            height: '100%',
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gridTemplateRows: '1fr 1fr',
            gap: 3,
            padding: 6,
            background: 'oklch(0.95 0.02 60)',
          }}
        >
          <div style={{ gridRow: '1 / 3', background: 'oklch(0.7 0.1 45)', borderRadius: 4 }} />
          <div style={{ background: 'oklch(0.8 0.08 60)', borderRadius: 4 }} />
          <div style={{ background: 'oklch(0.6 0.1 40)', borderRadius: 4 }} />
        </div>
      ),
    },
  ];

  return (
    <div className="screen">
      <div className="screen-header">
        <button className="icon-btn" onClick={onBack}>
          <Icon.ChevronRight size={20} />
        </button>
        <div className="step-counter">שלב 3 מתוך 5</div>
        <div style={{ width: 38 }} />
      </div>
      <div className="screen-scroll">
        <div className="eyebrow" style={{ marginTop: 24 }}>
          הסגנון של הסרטון
        </div>
        <h1 className="h1">איך זה ייראה?</h1>
        <p className="lede">בחרו תבנית עיצובית. תוכלו להחליף בכל שלב.</p>

        <div className="style-row">
          {styles.map((s) => (
            <div
              key={s.id}
              className={'style-card' + (value === s.id ? ' is-selected' : '')}
              onClick={() => onChange(s.id)}
            >
              <div className="preview">{s.preview}</div>
              <div className="meta">
                <div
                  className="name"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  {s.name}
                  {value === s.id && (
                    <span
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 999,
                        background: 'var(--accent)',
                        color: 'white',
                        display: 'grid',
                        placeItems: 'center',
                      }}
                    >
                      <Icon.Check size={11} />
                    </span>
                  )}
                </div>
                <div className="desc">{s.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 24 }}>
          <div className="h2">תכלית הסרטון</div>
          <p style={{ fontSize: 14, color: 'var(--ink-soft)', marginTop: 4, marginBottom: 12 }}>
            יוצרים נרטיב קצר בעברית שמלווה את התמונות.
          </p>
          <div className="card" style={{ padding: 14, cursor: 'default' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                color: 'var(--accent-deep)',
                fontFamily: 'Heebo',
                fontWeight: 500,
                fontSize: 13,
                marginBottom: 6,
              }}
            >
              <Icon.Sparkles size={14} /> נוצר אוטומטית בעזרת Gemini
            </div>
            <div style={{ fontSize: 15, lineHeight: 1.5, color: 'var(--ink)' }}>
              "הסיפור של דניאל התחיל ביום בו..."
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
              <span className="caption-chip">חם ומשפחתי</span>
              <span className="caption-chip">מצחיק</span>
              <span className="caption-chip">מרגש</span>
              <span className="caption-chip">+ בהתאמה אישית</span>
            </div>
          </div>
        </div>
      </div>
      <div className="cta-dock">
        <button className="btn-primary" disabled={!value} onClick={onNext}>
          המשך לבחירת מוזיקה
          <Icon.ArrowLeft size={18} />
        </button>
      </div>
    </div>
  );
}
